import React from "react";
import { Typography, StyledEngineProvider, Grid, Box, Link } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import PFATheme from "../../PFATheme";
import { MediaCard } from "../MediaCard";
import { AppUrl } from "../../config";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { appTitles } from "../../common/services/Utilities";
import NameOrLocationImg from "../../common/assets/images/Icon_Find_rest.svg";
import NameOrLocationImgHover from "../../common/assets/images/Icon_Find_focus.svg";
import ApiImg from "../../common/assets/images/Icon_API_rest.svg";
import ApiImgHover from "../../common/assets/images/Icon_API_focus.svg";
import EventsAndChangesImg from "../../common/assets/images/Icon_EC_rest.svg";
import EventsAndChangesImgHover from "../../common/assets/images/Icon_EC_focus.svg";
import HistoricalImg from "../../common/assets/images/Icon_HSOB_rest.svg";
import HistoricalImgHover from "../../common/assets/images/Icon_HSOB_focus.svg";
import FailuresImg from "../../common/assets/images/Icon_Failures_rest.svg";
import FailuresImgHover from "../../common/assets/images/Icon_Failures_focus.svg";
import HelpImage from "../../common/assets/images/Icon_Help_rest.svg";
import HelpImageHover from "../../common/assets/images/Icon_Help_focus.svg";
import SODImage from "../../common/assets/images/Icon_SOD_rest.svg";
import SODImageHover from "../../common/assets/images/Icon_SOD_focus.svg";
import FinancialsImg from "../../common/assets/images/Icon_FR_rest.svg";
import FinancialsImgHover from "../../common/assets/images/Icon_FR_focus.svg";
import AnalystsImg from "../../common/assets/images/Icon_Analysts.svg";
import ConsumersImg from "../../common/assets/images/Icon_Consumers.svg";
import BankersImg from "../../common/assets/images/Icon_Bankers.svg";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import { Navigate } from "react-router-dom";
import MediaCardMoreInoModal from "../MediaCardMoreInfoModal";

const theme = PFATheme;

const LandingPageIntro = (
  <>
    <Typography variant="h1">BankFind Suite</Typography>
    <Typography variant="body2">
      BankFind Suite is available for the public to search the FDIC’s extensive data records. BankFind Suite allows you to locate current and former FDIC-insured banking institutions by name, FDIC certificate number, website and/or by location. The Suite also allows a user to follow the history and financial trends of an individual institution, group of institutions, or the industry as a whole. Updates and improvements to functionality and data access are regularly released as the development progresses. {" "}
      <RouterLink id="release-notes-link" to={"/help?helpTopic=release-notes"}>
      View the release notes for past improvements
      </RouterLink>
      .
    </Typography>
    <Typography variant="body2" >
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=BankFindHome"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
  </>
);

const gridItem = {
  size: {
    md: 3,
    xs: 12,
    sm: 6,
  },
};

export default function BankFindSuite() {
  useInitialPageLoad("homePage", true);
  const urlParams = new URLSearchParams(window.location.search);
  const stateParam = urlParams.get("state") ?? "";
  const codeParam = urlParams.get("code") ?? "";
  const [redirect, setRedirect] = React.useState("/");
  const [hoveredCard, setHoveredCard] = React.useState<
    string | undefined | null
  >(null);

  React.useEffect(() => {
    if (stateParam) {
      const codePrefix = stateParam.includes("?") ? "&" : "?";
      const code = codeParam ? `${codePrefix}code=${codeParam}` : "";
      setRedirect(`${stateParam.split("/bankfind-suite")[1]}${code}`);
    }
  }, [codeParam, stateParam]);

  type AppKey =
    | "BankFind"
    | "FinancialReporting"
    | "OSCR"
    | "SOD"
    | "HSOB"
    | "Failures"
    | "BulkData"
    | "Help";

  type CardConfig = {
    key: AppKey;
    app: string;
    media: {
      img: string;
      hoverImg: string;
      id: string;
    };
    popover: {
      list1Title: string;
      list2Title: string;
      list1: string[];
      list2: string[];
      images: string[];
    };
  };
  const cards: CardConfig[] = [
    {
      key: "BankFind",
      app: "bankFind",
      media: {
        img: NameOrLocationImg,
        hoverImg: NameOrLocationImgHover,
        id: "bankFind",
      },
      popover: {
        list1Title: "",
        list2Title: "Institution Details including:",
        list1: [
          "Insurance Status",
          "Bank Search - Name/CERT/URL",
          "Location Search - City/State/ZIP",
          "Call Reports & UBPR",
          "Regulatory Identifiers",
          "Branch Offices",
        ],
        list2: [
          "Bank History Timelines",
          "Bank Status  - Active/Inactive",
          "Successor Information for Closed/Merged Banks",
        ],
        images: [ConsumersImg, BankersImg, AnalystsImg],
      },
    },
    {
      key: "FinancialReporting",
      app: "financials",
      media: {
        img: FinancialsImg,
        hoverImg: FinancialsImgHover,
        id: "financials",
      },
      popover: {
        list1Title: "",
        list2Title: "Custom Content Management:",
        list1: [
          "Financial Reports by Institution",
          " Compare Multiple Report Quarters",
          " Standard Comparison Reports",
          "External Links: Call & UBPR Reports",
        ],
        list2: [
          "Custom Financial Reports",
          "Custom Peer Groups",
          "Coming Soon: Data Charting",
        ],
        images: [BankersImg, AnalystsImg],
      },
    },
    {
      key: "SOD",
      app: "SOD",
      media: {
        img: SODImage,
        hoverImg: SODImageHover,
        id: "SOD",
      },
      popover: {
        list1Title: "",
        list2Title: "",
        list1: [
          "Annual Summary Tables",
          "Institution Market Presence",
          "Institution Growth Rate",
          "Regional Market Share",
        ],
        list2: [
          "Branch Office Deposits",
          "Pro-Forma Reporting",
          "Customized Data Download",
        ],
        images: [BankersImg, AnalystsImg],
      },
    },
    {
      key: "OSCR",
      app: "oscr",
      media: {
        img: EventsAndChangesImg,
        hoverImg: EventsAndChangesImgHover,
        id: "oscr",
      },
      popover: {
        list1Title: "Overview",
        list2Title: "Topics Include",
        list1: [
          "High-level Counts",
          " Customizable Time Range",
          "  Filtered by Bank Class",
          "Locational Filtering",
          " Institutions Involved",
          "Details of Events",
        ],
        list2: [
          "New Institutions",
          " Mergers & Acquisitions",
          " Trust Powers Granted",
          "Conversions & Title Changes",
          "Branch Office Openings/Closings",
        ],
        images: [BankersImg, AnalystsImg],
      },
    },
    {
      key: "HSOB",
      app: "historical",
      media: {
        img: HistoricalImg,
        hoverImg: HistoricalImgHover,
        id: "historical",
      },
      popover: {
        list1Title: "",
        list2Title: "",
        list1: [
          "Annualized Aggregations",
          "Timeframe-based Search",
          "Commercial & Savings Institutions",
        ],
        list2: ["Filter by Locality", "Bank Structure & Financial"],
        images: [BankersImg, AnalystsImg],
      },
    },
    {
      key: "Failures",
      app: "failures",
      media: {
        img: FailuresImg,
        hoverImg: FailuresImgHover,
        id: "failure",
      },
      popover: {
        list1Title: "",
        list2Title: "Details include:",
        list1: [
          "Timeframe-based Search",
          "Filter by Locality",
          " View by Detail, Year, or State",
          "Individual and Summary Totals",
          "Downloadable",
        ],
        list2: ["Insurance Type", "Bank Class", "Transaction Type"],
        images: [BankersImg, AnalystsImg],
      },
    },
    {
      key: "BulkData",
      app: "bulkData",
      media: {
        img: ApiImg,
        hoverImg: ApiImgHover,
        id: "API",
      },
      popover: {
        list1Title: "",
        list2Title: "",
        list1: [
          "Bulk Data Download",
          "API User Guide Documentation",
          "API Queries and Filters Syntax",
        ],
        list2: ["Output Formats", "Endpoints Used", "Coming Soon: API Keys"],
        images: [BankersImg, AnalystsImg],
      },
    },
    {
      key: "Help",
      app: "help",
      media: {
        img: HelpImage,
        hoverImg: HelpImageHover,
        id: "Help",
      },
      popover: {
        list1Title: "",
        list2Title: "",
        list1: [
          "Tutorials",
          "Variable Definitions",
          "Data Availability",
          "Disclaimers & Methodologies",
        ],
        list2: [
          "Historic Changes to Data",
          "New Site Functionality",
          "Site Release Notes",
        ],
        images: [ConsumersImg, BankersImg, AnalystsImg],
      },
    },
  ];

  return (
    <>
      <Navigate to={redirect} replace />
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle={"BankFind Suite"} />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          {LandingPageIntro}
          <Box sx={{ maxWidth: 1200, mx: "auto", width: "100%" }}>
            <Grid container spacing={2} sx={{ pb: 3 }}>
              {cards.map((card, index) => (
                <Grid key={card.key} {...gridItem}>
                  <MediaCard
                    img={card.media.img}
                    hoverImg={card.media.hoverImg}
                    title={appTitles(card.app)}
                    id={card.media.id}
                    href={AppUrl(card.key)}
                    hoveredCard={hoveredCard}
                    setHoveredCard={setHoveredCard}
                    moreInfo={
                      <MediaCardMoreInoModal
                        title={appTitles(card.app)}
                        list1Title={card.popover.list1Title}
                        list2Title={card.popover.list2Title}
                        list1={card.popover.list1}
                        list2={card.popover.list2}
                        images={card.popover.images}
                        href={AppUrl(card.key)}
                      />
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
}
